import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, BookOpen, Eye, EyeOff, Facebook, MessageSquare, Share2, Check } from 'lucide-react';

const BlogManagement = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [socialStatus, setSocialStatus] = useState({ facebook_configured: false, line_configured: false });
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: 'Market Updates',
    author: 'NOK Thailand Team',
    image_url: '',
    published: true,
    featured: false
  });

  useEffect(() => {
    fetchPosts();
    fetchSocialStatus();
  }, []);

  const fetchSocialStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/social/status`, { headers: { Authorization: `Bearer ${token}` } });
      setSocialStatus(res.data);
    } catch {}
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts`);
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      category: 'Market Updates',
      author: 'NOK Thailand Team',
      image_url: '',
      published: true,
      featured: false
    });
    setDialogOpen(true);
  };

  const openEditDialog = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt,
      category: post.category,
      author: post.author,
      image_url: post.image_url || '',
      published: post.published,
      featured: post.featured
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (editingPost) {
        await axios.put(`${API}/blog/posts/${editingPost.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post updated successfully');
      } else {
        await axios.post(`${API}/blog/posts`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post created successfully');
      }
      
      setDialogOpen(false);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/blog/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted successfully');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const postToFacebook = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/facebook/publish-blog/${postId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Posted to Facebook!');
      fetchPosts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to post to Facebook'); }
  };

  const postToLine = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/line/publish-blog/${postId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Posted to LINE Voom!');
      fetchPosts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to post to LINE'); }
  };

  const postToAll = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/social/publish-blog/${postId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.facebook?.success) toast.success('Posted to Facebook!');
      else if (res.data.facebook?.error) toast.error(`FB: ${res.data.facebook.error}`);
      if (res.data.line?.success) toast.success('Posted to LINE!');
      else if (res.data.line?.error) toast.error(`LINE: ${res.data.line.error}`);
      fetchPosts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to post'); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0E27] via-[#1a1f3a] to-[#0A0E27]">
      <header className="bg-black/40 backdrop-blur-md border-b border-[#D4AF37]/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl font-bold text-[#D4AF37]">Blog Management</h1>
              <p className="text-[#C9A961] text-sm">Educational Content & Weekly Insights</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={openCreateDialog}
                className="bg-gradient-to-r from-[#D4AF37] to-[#C9A961] hover:from-[#C9A961] hover:to-[#D4AF37] text-black font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Post
              </Button>
              <Button 
                onClick={() => navigate('/admin/dashboard')} 
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#D4AF37] border-r-transparent"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <div key={post.id} className="bg-gradient-to-br from-black/70 to-[#1a1f3a]/70 backdrop-blur-lg rounded-xl border-2 border-[#D4AF37]/30 overflow-hidden">
                {post.image_url && (
                  <img src={post.image_url} alt={post.title} className="w-full h-40 object-cover" />
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="px-2 py-1 bg-[#D4AF37] text-black text-xs font-bold rounded">{post.category}</span>
                    {post.featured && (
                      <span className="px-2 py-1 bg-orange-500 text-white text-xs font-bold rounded">Featured</span>
                    )}
                    {post.published ? (
                      <span className="px-2 py-1 bg-green-500 text-white text-xs font-bold rounded flex items-center gap-1">
                        <Eye className="w-3 h-3" /> Published
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-500 text-white text-xs font-bold rounded flex items-center gap-1">
                        <EyeOff className="w-3 h-3" /> Draft
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-lg font-bold text-[#D4AF37] mb-2 line-clamp-2">{post.title}</h3>
                  <p className="text-gray-300 text-sm mb-3 line-clamp-2">{post.excerpt}</p>
                  <p className="text-gray-500 text-xs mb-2">{formatDate(post.created_at)}</p>
                  <div className="flex items-center gap-2 mb-3">
                    {post.facebook_posted && <span className="inline-flex items-center gap-1 text-[10px] text-blue-400"><Check className="w-3 h-3" /> FB</span>}
                    {post.line_posted && <span className="inline-flex items-center gap-1 text-[10px] text-green-400"><Check className="w-3 h-3" /> LINE</span>}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button onClick={() => openEditDialog(post)} variant="outline" size="sm"
                      className="flex-1 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black">
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button onClick={() => handleDelete(post.id)} variant="outline" size="sm"
                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => postToFacebook(post.id)} variant="outline" size="sm"
                      disabled={post.facebook_posted}
                      className={`rounded-lg ${post.facebook_posted ? 'border-blue-500/40 text-blue-300 opacity-60' : 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10'}`}
                      title={post.facebook_posted ? 'Posted to Facebook' : 'Post to Facebook'}
                      data-testid={`fb-blog-${post.id}`}
                    >
                      {post.facebook_posted ? <Check className="w-3.5 h-3.5" /> : <Facebook className="w-3.5 h-3.5" />}
                    </Button>
                    <Button onClick={() => postToLine(post.id)} variant="outline" size="sm"
                      disabled={post.line_posted}
                      className={`rounded-lg ${post.line_posted ? 'border-green-500/40 text-green-300 opacity-60' : 'border-green-500/30 text-green-400 hover:bg-green-500/10'}`}
                      title={post.line_posted ? 'Posted to LINE' : 'Post to LINE Voom'}
                      data-testid={`line-blog-${post.id}`}
                    >
                      {post.line_posted ? <Check className="w-3.5 h-3.5" /> : <MessageSquare className="w-3.5 h-3.5" />}
                    </Button>
                    <Button onClick={() => postToAll(post.id)} variant="outline" size="sm"
                      disabled={post.facebook_posted && post.line_posted}
                      className={`rounded-lg ${post.facebook_posted && post.line_posted ? 'border-[#D4AF37]/20 text-[#D4AF37]/40 opacity-60' : 'border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10'}`}
                      title="Post to All Platforms"
                      data-testid={`post-all-blog-${post.id}`}
                    >
                      <Share2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && posts.length === 0 && (
          <div className="text-center py-20 bg-black/40 backdrop-blur-lg rounded-2xl border-2 border-[#D4AF37]/30">
            <BookOpen className="w-16 h-16 mx-auto text-[#D4AF37] mb-4" />
            <p className="text-xl text-gray-300 mb-4">No blog posts yet</p>
            <Button onClick={openCreateDialog} className="bg-gradient-to-r from-[#D4AF37] to-[#C9A961] text-black">
              Create Your First Post
            </Button>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#D4AF37]">
              {editingPost ? 'Edit Post' : 'Create New Post'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Enter post title"
              />
            </div>
            
            <div>
              <Label>Excerpt (Summary) *</Label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                placeholder="Brief summary of the post"
                rows={2}
              />
            </div>

            <div>
              <Label>Content *</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                placeholder="Full article content"
                rows={10}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Market Updates">Market Updates</SelectItem>
                    <SelectItem value="Insurance">Insurance</SelectItem>
                    <SelectItem value="Legal">Legal</SelectItem>
                    <SelectItem value="Investment Tips">Investment Tips</SelectItem>
                    <SelectItem value="Retirement Planning">Retirement Planning</SelectItem>
                    <SelectItem value="Tax Information">Tax Information</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Author</Label>
                <Input
                  value={formData.author}
                  onChange={(e) => setFormData({...formData, author: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Image URL or Upload (Optional)</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                placeholder="Paste image URL"
                className="mb-2"
              />
              <div className="text-sm text-gray-600">
                <p className="mb-1">💡 Upload to: <a href="https://imgur.com" target="_blank" className="text-blue-600 underline">Imgur</a> or <a href="https://postimages.org" target="_blank" className="text-blue-600 underline">PostImages</a></p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.published}
                  onChange={(e) => setFormData({...formData, published: e.target.checked})}
                  className="h-4 w-4"
                />
                <Label>Published</Label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                  className="h-4 w-4"
                />
                <Label>Featured Post</Label>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={handleSave} className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#C9A961] text-black">
                {editingPost ? 'Update Post' : 'Create Post'}
              </Button>
              <Button onClick={() => setDialogOpen(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogManagement;
